/**
 * Composant SimpleUpdateBanner pour Squash Scoring App
 * 
 * Bannière simple pour notifier les mises à jour disponibles
 * 
 * @module components/SimpleUpdateBanner
 * @version 1.0.0
 */

import React from 'react';
import { useSimpleUpdate } from '../hooks/useSimpleUpdate';
import { useTranslation } from '../i18n/hooks/useTranslation';

export const SimpleUpdateBanner: React.FC = () => {
  const { 
    showUpdate, 
    isUpdating, 
    newVersion, 
    currentVersion,
    applyUpdate, 
    dismissUpdate 
  } = useSimpleUpdate();

  const { t } = useTranslation();

  // Ne rien afficher si pas de mise à jour
  if (!showUpdate) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50">
      <div className="max-w-sm mx-auto bg-white border border-gray-200 p-4">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            
            {/* Icône */}
            <div className="w-8 h-8 bg-green-500 flex items-center justify-center flex-shrink-0">
              {isUpdating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <span className="text-white text-sm">🔄</span>
              )}
            </div>
            
            {/* Texte */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900">
                {isUpdating ? t('components.pwa.update.updating') : t('components.pwa.update.newVersion')}
              </h3>
              <p className="text-xs text-gray-500">
                {isUpdating ? t('components.pwa.update.pleaseWait') : (
                  <span>
                    {currentVersion} → {newVersion} {/* 🆕 AFFICHAGE VERSIONS */}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {/* Bouton fermer */}
          {!isUpdating && (
            <button
              onClick={dismissUpdate}
              className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label={t('common.close')}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          
          {/* Bouton principal de mise à jour */}
          <button
            onClick={applyUpdate}
            disabled={isUpdating}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              isUpdating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
            }`}
          >
            {isUpdating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('components.pwa.update.updating')}
              </span>
            ) : (
              `🔄 ${t('components.pwa.update.action')}`
            )}
          </button>

          {/* Bouton secondaire "Plus tard" */}
          {!isUpdating && (
            <button
              onClick={dismissUpdate}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              {t('components.pwa.update.later')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleUpdateBanner;